import React, { useEffect } from "react";

interface ToastNotificationProps {
  message: string;
  type?: "success" | "error" | "info";
  isVisible: boolean;
  onClose: () => void;
  duration?: number; // in milliseconds
  position?: "top" | "bottom";
}

const ToastNotification: React.FC<ToastNotificationProps> = ({
  message,
  type = "success",
  isVisible,
  onClose,
  duration = 3000,
  position = "bottom"
}) => {
  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible) return null;

  const positionClass = position === "top" 
    ? "top-4 sm:top-6" 
    : "bottom-6 sm:bottom-8";

  const typeClasses = {
    success: "bg-green-600 text-white",
    error: "bg-red-600 text-white", 
    info: "bg-neutral text-white"
  };

  const icons = {
    success: (
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        className="h-5 w-5" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M5 13l4 4L19 7" 
        />
      </svg>
    ),
    error: (
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        className="h-5 w-5" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M6 18L18 6M6 6l12 12" 
        />
      </svg>
    ),
    info: (
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        className="h-5 w-5" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
        />
      </svg>
    )
  };

  return (
    <div className={`fixed ${positionClass} left-1/2 transform -translate-x-1/2 z-50`}>
      <div 
        className={`
          ${typeClasses[type]} 
          px-4 py-3 sm:px-6 sm:py-4 
          rounded-lg shadow-lg 
          flex items-center gap-3
          min-w-[280px] max-w-[90vw] sm:max-w-md
          transition-all duration-300 ease-out
          ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
        `}
      >
        <span className="flex-shrink-0">
          {icons[type]}
        </span>
        <span className="text-sm sm:text-base font-medium flex-1">
          {message}
        </span>
        <button
          onClick={onClose}
          className="flex-shrink-0 p-1 hover:bg-base-100/20 rounded-full transition-colors"
          aria-label="Close notification"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-4 w-4" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M6 18L18 6M6 6l12 12" 
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ToastNotification;